/**
 * PublisherArticleView — canonical read page for a Publisher article.
 *
 * Route: /article/:slug (mounted for blogs.socialpulse.com)
 * Data:  GET /api/publisher/v1/widget/article/{slug}?site_id=blogspulse
 *
 * Renders title, cover, HTML body, and the ChatPulse discussion
 * anchored to this article's ID.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, User as UserIcon } from "lucide-react";
import ChatPulseThread from "@/components/chatpulse/ChatPulseThread";
import ListenToArticle from "@/components/publisher/ListenToArticle";
import ReadingProgressBar from "@/components/publisher/ReadingProgressBar";
import ReadTimeChip from "@/components/publisher/ReadTimeChip";
import ClapButton from "@/components/publisher/ClapButton";
import AuthorFollowCard from "@/components/publisher/AuthorFollowCard";
import AuthorFollowMoreLink from "@/components/publisher/AuthorFollowMoreLink";
import ArticleShareCard from "@/components/publisher/ArticleShareCard";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function timeAgo(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function PublisherArticleView() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, article: null, body_html: null, error: null });
  const [suggestions, setSuggestions] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/widget/article/${slug}?site_id=blogspulse`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setState({ loading: false, article: d.article, body_html: d.body_html || d.article.body_html || null, error: null });
        // Fire-and-forget view counter (once per article render).
        if (alive && d.article?.id) {
          fetch(`${BASE}/articles/${d.article.id}/view`, { method: "POST" }).catch(() => {});
        }
      } catch (e) {
        if (alive) setState({ loading: false, article: null, body_html: null, error: e.message });
        // On failure, try to resolve a close-matching slug so we can
        // render "Did you mean…?" instead of a bare 404.
        try {
          const rr = await fetch(`${BASE}/article/${slug}/resolve`);
          if (rr.ok) {
            const dd = await rr.json();
            if (alive) setSuggestions(dd.suggestions || []);
          }
        } catch { /* noop */ }
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // OG meta tags — set once article resolves so shares on socials render.
  useEffect(() => {
    if (!state.article) return;
    const a = state.article;
    const set = (prop, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    document.title = `${a.title} — SocialPulse.blog`;
    set("og:title", a.title);
    set("og:description", a.summary || "");
    set("og:image", a.cover_image_url || "");
    set("og:type", "article");
    set("og:url", a.canonical_url || window.location.href);
    set("article:author", a.author_id || "");
    set("article:section", a.primary_category || "");
    set("article:published_time", a.published_at || "");
  }, [state.article]);

  if (state.loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading article…
      </div>
    );
  }

  if (state.error || !state.article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-slate-600" data-testid="article-not-found">
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-900 mb-2">Article not found</p>
          <p className="text-sm mb-4">
            The slug <code className="bg-slate-100 px-1 rounded">{slug}</code> doesn't match any published article.
          </p>
        </div>

        {suggestions && suggestions.length > 0 && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 md:p-5" data-testid="article-suggestions">
            <p className="text-sm font-semibold text-slate-900 mb-3">Did you mean…?</p>
            <ul className="space-y-2">
              {suggestions.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/article/${s.slug}`}
                    className="block rounded-xl px-3 py-2 hover:bg-slate-50 transition"
                    data-testid={`article-suggestion-${s.slug}`}
                  >
                    <div className="text-sm font-semibold text-slate-900">{s.title}</div>
                    {s.summary && (
                      <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{s.summary}</div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1">
                      /article/{s.slug}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-center mt-6">
          <Link to="/publisher/notes" className="text-blue-700 hover:underline text-sm inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to Publisher
          </Link>
        </div>
      </div>
    );
  }

  const a = state.article;
  return (
    <>
      <ReadingProgressBar targetSelector="[data-reading-target]" />
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
      <article
        className="min-w-0"
        data-testid="publisher-article-view"
        data-reading-target
      >
      <header className="mb-6 pb-5 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5 mb-1">
          <span>{a.primary_category}</span>
          {a.editorial_label && (
            <>
              <span className="text-slate-300">·</span>
              <span>{a.editorial_label}</span>
            </>
          )}
          {a.primary_location_id && (
            <>
              <span className="text-slate-300">·</span>
              <span>{a.primary_location_id}</span>
            </>
          )}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight"
          data-testid="publisher-article-title"
        >
          {a.title}
        </h1>
        {a.summary && (
          <p className="mt-3 text-base text-slate-600 leading-relaxed">{a.summary}</p>
        )}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {a.author_id && <AuthorFollowCard authorId={a.author_id} variant="inline" fallbackName={a.author_display_name} />}
          <span className="text-xs text-slate-400">·</span>
          {a.published_at && <time className="text-xs text-slate-500">{new Date(a.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</time>}
          <ReadTimeChip articleId={a.id} />
        </div>
      </header>

      {a.cover_image_url && (
        <img
          src={a.cover_image_url}
          alt=""
          className="w-full rounded-2xl mb-6 border border-slate-200"
        />
      )}

      <ListenToArticle slug={slug} />

      {state.body_html ? (
        <div
          className="pub-prose prose prose-slate max-w-none text-base leading-relaxed [&_p]:my-4 [&_a]:text-blue-700 [&_a]:underline [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-6 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: state.body_html }}
          data-testid="publisher-article-body"
        />
      ) : (
        <p className="italic text-slate-500">
          Full text is available on partner sites — check the widgets embedded on
          NewsPulse, FarmersPulse, and FoodPulse.
        </p>
      )}

      {/* Clap + share bar after body — Medium/Substack parity */}
      <div className="mt-10 pt-6 border-t border-slate-200 flex items-center justify-between gap-4 flex-wrap">
        <ClapButton articleId={a.id} />
        {a.author_id && <AuthorFollowMoreLink authorId={a.author_id} />}
      </div>

      {/* Big footer author-follow card */}
      {a.author_id && <AuthorFollowCard authorId={a.author_id} variant="card" fallbackName={a.author_display_name} />}

      {/* ChatPulse — anchored to the article's ID so the same thread
          surfaces wherever the article is embedded. */}
      <ChatPulseThread
        contextType="article"
        contextId={a.id}
        contextLabel={a.title}
      />
    </article>

    {/* Right sidebar (desktop): share card sticks in view; on mobile
        the whole grid collapses so this appears below the article. */}
    <aside className="min-w-0 lg:sticky lg:top-6 self-start" aria-label="Article share panel">
      <ArticleShareCard
        articleId={a.id}
        title={a.title}
        canonicalUrl={a.canonical_url || (typeof window !== "undefined" ? window.location.href : "")}
      />
    </aside>
    </div>
    </>
  );
}
